import React from "react";

import { useIsBot } from "./IsBotContext";

/**
 * This hook tracks whether an element is in the viewport.
 * Along with the node and an initial value, it takes in an optional options object containing the same options
 * as the IntersectionObserver constructor, which it relies upon internally.
 *
 * Original source: https://usehooks.com/useOnScreen/
 */
export default function useOnScreen(
  node?: Element | null,
  initialValue = false,
  options: IntersectionObserverInit = {},
) {
  const [isIntersecting, setIntersecting] = React.useState(initialValue);
  const isPrerenderRequest = useIsBot();

  React.useEffect(() => {
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setIntersecting(entry.isIntersecting);
    }, options);

    observer.observe(node);

    // Check if initially intersecting (fixes race condition for elements already in viewport)
    const entries = observer.takeRecords();
    if (entries.length > 0) {
      setIntersecting(entries[0].isIntersecting);
    }

    return () => {
      observer.unobserve(node);
    };

    // Use the values of the options object to determine when to re-run the effect.
    // Using the object itself could cause extraneous re-renders because it's a reference type.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, ...Object.values(options)]);

  return isPrerenderRequest || isIntersecting;
}
